import React, {
    useState,
    useCallback,
    useEffect,
    useContext,
    useRef
} from 'react';

import { useHistory } from 'react-router-dom';

import {
    isEmailAddress,
    isIpv4Address,
    returnHashType,
    isDomain,
    isUrlAddress
} from '~/modules/validation';

import { toErrorMessage } from '~/modules/helpers/error';
import { returnCorrectPath, urlSeparator } from '~/modules/manageRouting';

import Object from '~/models/Object';
import ObjectModel from '~/models/Object';
import AutomaticAction from '~/models/AutomaticAction';

import Icon from '~/components/Icon/Icon';
import Translate from '~/components/Translate/Translate';
import { cls_autoplaceholderContainer } from '~/components/Editor2/plugins/autoplaceholder/constants';

import AddObjectModal from '~/pages/IncidentDetails/Objects/Modals/AddObjectModal';

import ExecuteActionModal from '~/containers/ExecuteActionModal/ExecuteActionModal';
import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';

import InvolvedIncidents from './InvolvedIncidents/InvolvedIncidents';

const ContextualMenu = () => {
    const [showRunActionModal, setShowRunActionModal] = useState({
        actionList: {},
        show: false
    });
    const [showAddObjectModal, setShowAddObjectModal] = useState({
        data: {},
        show: false
    });
    const [showInvolvedIncidentsModal, setShowInvolvedIncidentsModal] =
        useState(false);

    const {
        anchorPoint,
        show,
        setShow,
        data,
        objectType,
        objectResponse,
        incidentId
    } = useContextMenu();
    const objectExist = !!objectResponse;
    let objectIsLocked, objectIsDeleted;
    if (objectExist) {
        objectIsLocked = objectResponse?.locked;
        objectIsDeleted = objectResponse?.deleted;
    }
    const stickyAlertContext = useContext(StickyAlertContext);
    const history = useHistory();

    const showDetailAction = (openInOtherTab = false, entityId) => {
        let link;
        if (objectExist || entityId) {
            let id = entityId || objectResponse?.id;
            link = `${returnCorrectPath(
                'entitiesDetails',
                false,
                id,
                'objects'
            )}`;
            if (!!incidentId) {
                link = `${returnCorrectPath(
                    'incidentsObjects',
                    false,
                    incidentId
                )}${urlSeparator}${id}`;
            }
            if (openInOtherTab) {
                window.open(link, '_blank', 'noopener,noreferrer');
            } else {
                history.push(link);
            }
            setShow(false);
        }
    };

    if (show) {
        const copyText = entityValue => {
            // noinspection JSIgnoredPromiseFromCall
            navigator.clipboard.writeText(entityValue);
            stickyAlertContext.addStickyAlert({
                title: '',
                status: 'success',
                message: <Translate message="Copied" />
            });
            setShow(false);
        };

        const runAction = () => {
            const responsesData = {
                actionList: {}
            };
            Object.retrieveAvailableActionsByType(objectType.type).then(
                response => {
                    responsesData.actionList = response.data;
                    for (const type of [
                        'Enrichment',
                        'Containment',
                        'Custom',
                        'Notification',
                        'Scheduled'
                    ]) {
                        responsesData.actionList[type] = response.data[
                            type
                        ]?.reduce((acc, current) => {
                            const str = current.name.trim().toUpperCase();
                            const foundIndex = acc.findIndex(
                                e => str === e.name.trim().toUpperCase()
                            );
                            if (-1 !== foundIndex) {
                                const internalName = current.internalName;
                                current.resources.forEach(e => {
                                    e.actionInternalName = internalName;
                                });
                                const found = acc[foundIndex];
                                found.resources = [
                                    ...found.resources,
                                    ...current.resources
                                ];
                                return acc;
                            } else {
                                return acc.concat([current]);
                            }
                        }, []);
                    }

                    if (objectExist) {
                        responsesData.object = new ObjectModel(
                            objectResponse.id
                        );
                    } else {
                        responsesData.object = new ObjectModel();
                        responsesData.object.extendedType = objectType.type;
                    }
                    responsesData.object.type = objectType.internalType;
                    responsesData.object.value = data.highlightedText;

                    if (!!incidentId) {
                        responsesData.object.incidentId = incidentId;

                        AutomaticAction.getPlaceholderList(incidentId).then(
                            response => {
                                responsesData.tokenList = response.data;
                            }
                        );
                    }
                    setShowRunActionModal({
                        ...responsesData,
                        show: true
                    });
                    setShow(false);
                }
            );
        };

        const RenderRunActionIfNotLockedOrDeleted = ({}) => {
            if (objectIsDeleted && objectIsLocked) {
                return (
                    <>
                        <div className="unavailable-action">
                            <Translate message="Entity is deleted and locked" />
                        </div>
                        <hr />
                    </>
                );
            } else if (objectIsDeleted) {
                return (
                    <>
                        <div className="unavailable-action">
                            <Translate message="Entity is deleted" />
                        </div>
                        <hr />
                    </>
                );
            } else if (objectIsLocked) {
                return (
                    <>
                        <div className="unavailable-action">
                            <Translate message="Entity is locked" />
                        </div>
                        <hr />
                    </>
                );
            }

            return (
                <li onClick={runAction}>
                    <Icon type="play-circle" />
                    <Translate message="Run action" />
                </li>
            );
        };

        /*
         * label: "File" value: "artifacts"
         * HASH
         * label: "IP" value: "ip-addresses"
         * label: "Domain" value: "domain"
         * label: "URL" value: "url-addresses"
         * label: "Email" value: "mail-addresses"
         *
         *
         * */
        return (
            <div
                id="contextual-menu-actions-list"
                style={{
                    top: anchorPoint.top,
                    right: anchorPoint.right,
                    bottom: anchorPoint.bottom,
                    left: anchorPoint.left
                }}
            >
                <ul>
                    <RenderRunActionIfNotLockedOrDeleted />
                    {!objectExist && (
                        <li
                            onClick={() => {
                                setShowAddObjectModal({
                                    data,
                                    show: true
                                });
                                setShow(false);
                            }}
                        >
                            <Icon type="plus" />
                            <Translate message="Create entity" />
                        </li>
                    )}
                    {objectExist &&
                        !window.location.href.indexOf(objectResponse.id) >
                            -1 && (
                            <li
                                onClick={() => {
                                    showDetailAction(false);
                                }}
                            >
                                <Icon type="magnifier" />
                                <Translate message="Show details" />
                            </li>
                        )}
                    {objectType &&
                        (objectType.type === 'url' ||
                            objectType.type === 'domain') && (
                            <li
                                onClick={() => {
                                    window.open(
                                        objectType.type === 'url'
                                            ? data.highlightedText
                                            : `https://${data.highlightedText}`,
                                        '_blank'
                                    );
                                }}
                            >
                                <Icon type="star" />
                                <Translate message="Open link" />
                            </li>
                        )}
                    {objectExist &&
                        objectResponse?.involved_incidents?.count > 0 && (
                            <li
                                onClick={() => {
                                    setShowInvolvedIncidentsModal(true);
                                    setShow(false);
                                }}
                            >
                                <Icon type="shield-check2" />
                                <Translate message="Involved incidents" />
                            </li>
                        )}
                    <li
                        onClick={() => {
                            copyText(data.highlightedText);
                        }}
                    >
                        <Icon type="copy" />
                        <Translate message="Copy text" />
                    </li>
                </ul>
            </div>
        );
    }
    return (
        <>
            {!!showAddObjectModal?.show && (
                <AddObjectModal
                    initialData={showAddObjectModal?.data}
                    show={showAddObjectModal?.show}
                    onHide={() => {
                        setShowAddObjectModal({ data: {}, show: false });
                    }}
                    incidentId={!!incidentId ? incidentId : undefined}
                />
            )}
            {!!showRunActionModal?.show && (
                <ExecuteActionModal
                    initialData={showRunActionModal?.data}
                    show={showRunActionModal?.show}
                    onHide={() => {
                        setShowRunActionModal({
                            actionList: {},
                            tokenList: {},
                            show: false
                        });
                    }}
                    actionList={showRunActionModal?.actionList}
                    tokenList={showRunActionModal?.tokenList}
                    object={showRunActionModal?.object}
                    fromContextualMenu={true}
                    onRunActionSubmit={showDetailAction}
                />
            )}
            {!!showInvolvedIncidentsModal && (
                <InvolvedIncidents
                    show={showInvolvedIncidentsModal}
                    onHide={() => {
                        setShowInvolvedIncidentsModal(false);
                    }}
                    involvedIncidents={objectResponse.involved_incidents}
                />
            )}
        </>
    );
};

const useContextMenu = () => {
    const [anchorPoint, setAnchorPoint] = useState({
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
    });
    const incidentSectionRegEx = SETTINGS.NEWUI
        ? /cases\/\d+/
        : /incident\|\d+/;
    const [show, setShow] = useState(false);
    const [data, setData] = useState({});
    const [objectType, setObjectType] = useState(null);
    const [objectResponse, setObjectResponse] = useState({});
    const [incidentId, setIncidentId] = useState(0);

    const mouseUpEvent = useRef(null);

    const stickyAlertContext = useContext(StickyAlertContext);

    const checkIsIncidentSection = () => {
        const isIncident = incidentSectionRegEx.test(
            window.location.hash || window.location.pathname
        );
        let incidentId = 0;
        if (isIncident) {
            incidentId = window.location.hash
                ? window.location.hash.split('|')[1]
                : window.location.pathname.split('/')[2];
        }
        setIncidentId(incidentId);
    };

    const resetDataAndClose = () => {
        setData({});
        setObjectType(null);
        setObjectResponse({});
        setShow(false);
    };

    const setMenuPosition = useCallback(
        selection => {
            //RN: set position of menu
            let selectionPosition = selection;
            if (selectionPosition && selectionPosition.rangeCount > 0) {
                selectionPosition = selectionPosition
                    .getRangeAt(0)
                    .getBoundingClientRect();
            } else {
                selectionPosition = {
                    x: 0,
                    y: 0
                };
            }
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;
            const position = {};
            const useElementPosition =
                !!selectionPosition.x && !!selectionPosition.y;
            //RN: use mouse position if height of selection is >= 50px to avoid bad position of Menu
            const oneLineText = selectionPosition < 50;

            if (useElementPosition && oneLineText) {
                //RN: if mouse X is over the middle of the page set menu to the left else to the right
                if (selectionPosition.x > windowWidth / 2) {
                    //RN: set to left
                    position.right = windowWidth - selectionPosition.left;
                } else {
                    //RN: set to right
                    position.left = selectionPosition.right;
                }

                //RN: if mouse Y is in the top middle of the page set menu to the bottom else to the top
                if (selectionPosition.y > windowHeight / 2) {
                    //RN: set to top
                    position.bottom = windowHeight - selectionPosition.top;
                } else {
                    //RN: set to bottom
                    position.top = selectionPosition.bottom;
                }
            } else {
                //RN: if text is selected through a input/textarea selection infos are not available with selectPosition
                //RN: if mouseUp X is over the middle of the page set menu to the left else to the right
                if (mouseUpEvent.current.x > windowWidth / 2) {
                    //RN: set to left
                    position.right = windowWidth - mouseUpEvent.current.x;
                } else {
                    //RN: set to right
                    position.left = mouseUpEvent.current.x;
                }

                //RN: if mouseUp Y is in the top middle of the page set menu to the bottom else to the top
                if (mouseUpEvent.current.y > windowHeight / 2) {
                    //RN: set to top
                    position.bottom =
                        windowHeight - mouseUpEvent.current.y + 10;
                } else {
                    //RN: set to bottom
                    position.top = mouseUpEvent.current.y + 10;
                }
            }

            setAnchorPoint({
                top: position.top,
                right: position.right,
                bottom: position.bottom,
                left: position.left
            });
        },
        [show, mouseUpEvent?.current, setAnchorPoint]
    );

    const handleContextMenu = useCallback(
        event => {
            event.preventDefault();
            const cls = event?.target?.classList;
            // console.log({ event, cls });
            if (
                cls &&
                (cls.contains('ck') || // ckeditor ui elements
                    cls.contains('ag-')) // may be an SVGAnimatedString
            ) {
                // event.stopImmediatePropagation(); -> interfer with image resize
                event.stopPropagation();
                return;
            }

            mouseUpEvent.current = event;
            const selection = window.getSelection();

            if (selection.rangeCount) {
                const el = selection.getRangeAt(0).commonAncestorContainer;
                if (el) {
                    const doc = el.ownerDocument;
                    if (doc.hasOwnProperty('data-ck-expando')) {
                        // is ckeditor
                        if (
                            doc.querySelector(
                                `.${cls_autoplaceholderContainer}`
                            )
                        ) {
                            // console.warn(selection);
                            return; // ignore because placeholder creation
                        }
                    }
                }
            }

            const highlightedText = selection
                .toString()
                .trim()
                .replace(/(\r\n|\n|\r)/gm, '');

            //RN: disappear if I click outside of menu
            if (
                show &&
                !event.target.closest('#contextual-menu-actions-list') // target?
            ) {
                //RN: remove text selection after menu is showed
                if (selection.toString().length) {
                    if (selection.empty) {
                        // Chrome
                        selection.empty();
                    } else if (selection.removeAllRanges) {
                        // Firefox
                        selection.removeAllRanges();
                    }
                }
                resetDataAndClose();
                return;
            }

            if (highlightedText && highlightedText.length) {
                let objectData = null;
                let objectType = null;

                //RN: show contextual menu if selection match with some Entity
                if (isEmailAddress(highlightedText)) {
                    objectData = {
                        objType: {
                            label: 'Email',
                            value: 'mail-addresses'
                        },
                        objValue: [
                            {
                                deletable: false,
                                label: highlightedText,
                                value: highlightedText
                            }
                        ],
                        isValidForm: [true],
                        highlightedText
                    };
                    objectType = { type: 'email', internalType: 'EMAIL' };
                }
                if (isIpv4Address(highlightedText)) {
                    objectData = {
                        objType: {
                            label: 'IP',
                            value: 'ip-addresses'
                        },
                        objValue: [
                            {
                                deletable: false,
                                label: highlightedText,
                                value: highlightedText
                            }
                        ],
                        isValidForm: [true],
                        highlightedText
                    };
                    objectType = { type: 'ip', internalType: 'IP' };
                } else if (isUrlAddress(highlightedText)) {
                    objectData = {
                        objType: {
                            label: 'URL',
                            value: 'url-addresses'
                        },
                        objValue: [
                            {
                                deletable: false,
                                label: highlightedText,
                                value: highlightedText
                            }
                        ],
                        isValidForm: [true],
                        highlightedText
                    };
                    objectType = { type: 'url', internalType: 'URL' };
                }
                if (isDomain(highlightedText)) {
                    objectData = {
                        objType: { label: 'Domain', value: 'domain' },
                        objValue: [
                            {
                                deletable: false,
                                label: highlightedText,
                                value: highlightedText
                            }
                        ],
                        isValidForm: [true],
                        highlightedText
                    };
                    objectType = { type: 'domain', internalType: 'DOMAIN' };
                }
                let hash = returnHashType(highlightedText);
                switch (hash) {
                    case 'md5':
                        objectData = {
                            objType: { label: 'File', value: 'artifacts' },
                            selectedHashType: {
                                label: 'Hash MD5',
                                value: 'md5'
                            },
                            objFileData: { md5_hash: highlightedText },
                            isValidForm: [true],
                            highlightedText
                        };
                        objectType = { type: 'md5', internalType: 'HASH' };
                        break;
                    case 'sha256':
                        objectData = {
                            objType: { label: 'File', value: 'artifacts' },
                            selectedHashType: {
                                label: 'Hash SHA256',
                                value: 'sha256'
                            },
                            objFileData: { sha256_hash: highlightedText },
                            isValidForm: [true],
                            highlightedText
                        };
                        objectType = { type: 'sha256', internalType: 'HASH' };
                        break;
                    case 'sha1':
                        objectData = {
                            objType: { label: 'File', value: 'artifacts' },
                            selectedHashType: {
                                label: 'Hash SHA1',
                                value: 'sha1'
                            },
                            objFileData: { sha1_hash: highlightedText },
                            isValidForm: [true],
                            highlightedText
                        };
                        objectType = { type: 'sha1', internalType: 'HASH' };
                        break;
                }
                if (objectData) {
                    Object.validateValue(objectType.type, highlightedText)
                        .then(response => {
                            if (response.status === 200) {
                                const params = {
                                    value: highlightedText,
                                    incident_id: incidentId,
                                    involved_incidents: true
                                };
                                //RN: type can be ip, url, email, domain, sha1, md5 or sha256
                                Object.checkEntityExist(objectType.type, params)
                                    .then(response => {
                                        setMenuPosition(selection);
                                        setObjectResponse(
                                            response.data.details
                                        );
                                        setData({ ...data, ...objectData });
                                        setObjectType(objectType);
                                        setShow(true);
                                    })
                                    .catch(exception => {
                                        if (APP_INFO.debug) {
                                            console.debug(exception);
                                        }
                                        stickyAlertContext.addStickyAlert({
                                            status: 'danger',
                                            title: (
                                                <Translate message="Check entity exist" />
                                            ),
                                            message: toErrorMessage(
                                                exception,
                                                <Translate message="An error occurred while checking if entity exist" />
                                            )
                                        });
                                    });
                            }
                        })
                        .catch(exception => {
                            //RN: evaluate to print a error message if entity is not valid
                            console.error(exception);
                        });
                }
            }
        },
        [show, setShow, incidentId]
    );

    const onSelectionChange = useCallback(() => {
        const selection = window.getSelection().toString();
        if (show && data.highlightedText !== selection) {
            resetDataAndClose();
        }
    }, [show, data]);

    useEffect(() => {
        checkIsIncidentSection();
        document.addEventListener('mouseup', handleContextMenu);
        window.addEventListener('popstate', checkIsIncidentSection);
        return () => {
            document.removeEventListener('mouseup', handleContextMenu);
            window.removeEventListener('popstate', checkIsIncidentSection);
        };
    });

    useEffect(() => {
        if (show) {
            document.addEventListener('keyup', onSelectionChange);
        }
        return () => {
            document.removeEventListener('keyup', onSelectionChange);
        };
    }, [show]);

    return {
        anchorPoint,
        show,
        setShow,
        data,
        objectType,
        objectResponse,
        incidentId
    };
};

export default ContextualMenu;
